import { Button, Caption1, makeStyles } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { shellMedia, tokens, ShellIcons } from '@vigeo/ui';
import { NewCandidateForm } from '@/features/candidates';
import { fetchAuthState } from '@/lib/auth';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, minWidth: 0 },
  head: {
    display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL, flexWrap: 'wrap', minWidth: 0,
  },
  titles: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  /** Page-name voice (24/700), as on Offers, People and Review. */
  title: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightBold },
  /** Frame `289:291`: one card holding the intro and every row. */
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadowCard,
    display: 'flex', flexDirection: 'column', minWidth: 0,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    paddingBottom: tokens.spacingVerticalL,
  },
  /** `289:292` — the heading and the required-fields line, above the first rule. */
  intro: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalL, paddingBottom: tokens.spacingVerticalM,
  },
  introHeading: { margin: 0, fontSize: tokens.fontSizeBase500, lineHeight: tokens.lineHeightBase500, fontWeight: tokens.fontWeightSemibold },
  action: { [shellMedia.phone]: { width: '100%' } },
});

/**
 * Add candidate (#258, frame 06b `289:152`).
 *
 * A FULL PAGE, which is what the frame draws — the shell's breadcrumb, a page header with the CSV action, then
 * one card whose rows each put their label and description in a column beside the fields. It was
 * built as an inline panel inside the Candidates list, and no amount of correcting the fields
 * inside it made it read as the designed screen: the structure is what you see first.
 *
 * The form itself is unchanged and still owns its own footer — "Next: prepare their offer.",
 * Cancel, Add candidate — because the frame puts those inside the card, not on the page.
 */
export function AddCandidatePage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const auth = useQuery({ queryKey: ['auth'], queryFn: fetchAuthState, retry: false });
  const canEdit = auth.data?.status === 'signed-in' && auth.data.user.isAdmin;

  const backToList = () => { navigate('/app/people'); };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.titles}>
          <h1 className={styles.title}>Add candidate</h1>
          <Caption1>
            Add someone after their interview. Their record starts here, and you&rsquo;ll prepare their offer next.
          </Caption1>
        </div>
        {/* The frame's secondary header action. A Viewer is never offered it. */}
        {canEdit ? (
          <Button
            appearance="secondary"
            icon={<ShellIcons.upload />}
            className={styles.action}
            onClick={() => { navigate('/app/people', { state: { intake: 'import' } }); }}
          >
            Import a CSV
          </Button>
        ) : null}
      </header>

      <section className={styles.card} aria-labelledby="candidate-details">
        <div className={styles.intro}>
          <h2 id="candidate-details" className={styles.introHeading}>Candidate details</h2>
          <Caption1>
            {canEdit
              ? 'Fields marked * are required. Everything else can be filled in later from their application.'
              : 'View only. Adding a candidate is an administrator action, so the form is not offered here.'}
          </Caption1>
        </div>
        {/*
          The form is gated, not just the header action. The server already refuses
          (`'people.create': ADMIN`), and the repo's standing promise is that a Viewer is never
          shown a control the server will refuse — including a disabled stand-in for one.
        */}
        {canEdit ? <NewCandidateForm onCreated={backToList} onCancel={backToList} /> : null}
      </section>
    </div>
  );
}
